/**
 * HomeDesk — the /f8chat home state (PPL-416, spec §6 W-H1 / W-H2, §9).
 *
 * Zones A–E inside the existing 600 px `.f8-home-inner` column; zone F is
 * the page's own composer, outside this component and untouched. The page
 * mounts `<HomeDesk>` in place of its `chatMode === 'initial'` block when
 * the `homeDesk` flag is on, and nothing else changes.
 *
 * What the desk owns: opener tap → prefill, prompt tap → send (hover "edit
 * first" → prefill), dismiss / undo, the overflow expansion, S0's two paths,
 * and the analytics in spec §11. Every module action goes through the route
 * map in useHomeActions.ts (PPL-417): `open` becomes a route, a composer
 * prefill or a thread to resume; the verbs become callables (keep / skip,
 * rsvp), the desk's dismiss, or a re-fetch.
 *
 * Every contextual string is the payload's. The static lines are exactly
 * the ones the build allows (HOME_STATIC in homeStatic.ts), listed in the
 * PR.
 */

import { useCallback, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Compass, Smartphone, Users } from 'react-feather';
import type { HomeFeed, HomeModule, HomeOpener as HomeOpenerData } from '../../types/home';
import type { HomeModuleAction } from './moduleTypes';
import {
  HomeChevron,
  HomeDesk as HomeDeskColumn,
  HomeDeskSkeleton,
  HomeMoreRow,
  HomeOpener,
  HomeOpenerSkeleton,
  HomePill,
  HomePrompts,
  HomeReason,
  HomeTappable,
  HomeUndoRow,
  stop,
} from './HomeKit';
import { HomeModuleView } from './moduleRegistry';
import { formatClock } from './format';
import { HOME_STATIC } from './homeStatic';
import { HomeInlineContext } from './inlineState';
import { trackModuleTapped, trackOpenerTapped, trackPromptTapped } from './homeAnalytics';
import { intentTarget, todayPrefillFor, useHomeActions } from './useHomeActions';
import { useHomeDesk, type HomeDeskStatus } from './useHomeDesk';

export { HOME_STATIC, todayPrefillFor };

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── the column wrapper: the page's .f8-home / .f8-home-inner, desk-flavoured ──

function DeskFrame({ centred, children }: { centred: boolean; children: ReactNode }) {
  return (
    <div className={cx('f8-home', 'f8-home--desk', centred && 'f8-home--centred')}>
      <div className={cx('f8-home-inner', 'home-page', centred && 'home-page--centred')}>{children}</div>
    </div>
  );
}

// ── S0 · two paths, one screen (O4 resolved) ──────────────────────────

function S0Paths({ onMeet }: { onMeet: () => void }) {
  return (
    <div className="home-paths">
      {/* Contacts sync is app-only on web: the path is the WebAppChip's destination. */}
      <Link to="/download" className="home-path home-path--link">
        <span className="home-path-glyph" aria-hidden="true">
          <Users size={17} />
        </span>
        <span className="home-path-main">
          <span className="home-path-body">{HOME_STATIC.bringPpl}</span>
          <span className="home-path-sub">{HOME_STATIC.bringPplSub}</span>
        </span>
        <span className="home-setup-pill">
          <Smartphone size={12} aria-hidden="true" />
          {HOME_STATIC.bringPplPill}
        </span>
      </Link>
      <HomeTappable className="home-path" onOpen={onMeet} label={HOME_STATIC.meetPpl}>
        <span className="home-path-glyph home-path-glyph--magenta" aria-hidden="true">
          <Compass size={16} />
        </span>
        <span className="home-path-main">
          <span className="home-path-body">{HOME_STATIC.meetPpl}</span>
          <span className="home-path-sub">
            {HOME_STATIC.meetPplDiscover} ·{' '}
            <Link to="/posts" className="home-path-sub-link" onClick={stop}>
              {HOME_STATIC.meetPplPosts}
            </Link>
          </span>
        </span>
        <HomeChevron size={13} />
      </HomeTappable>
    </div>
  );
}

// ── the presentational desk — every zone from state + callbacks ────────

export interface HomeDeskViewProps {
  status: HomeDeskStatus;
  feed: HomeFeed | null;
  /** The visible modules, in server order (+ overflow once expanded). */
  modules: HomeModule[];
  openerPending: boolean;
  offline: boolean;
  expanded: boolean;
  undo: { text: string; onUndo: () => void } | null;
  /** "N previous chats" in zone E. */
  chats: number;
  onOpenerTap: (opener: HomeOpenerData) => void;
  onPromptPick: (text: string, index: number) => void;
  onPromptEdit: (text: string, index: number) => void;
  onModuleAction: (action: HomeModuleAction, module: HomeModule, position: number) => void;
  onExpandOverflow: () => void;
  onPreviousChats: () => void;
  onMeetPpl: () => void;
}

export function HomeDeskView(props: HomeDeskViewProps) {
  const { status, feed, modules, openerPending, offline, expanded, undo, chats } = props;
  const loading = status === 'loading';
  const stale = status === 'stale';
  const isS0 = feed?.stage === 'S0';
  // Quiet (opener + prompts + composer) and S0 sit in the middle of the
  // column; a desk stacks from the top. The bare error line is quiet too.
  const centred = !loading && (!feed || isS0 || (feed.quietDay && modules.length === 0 && !undo));
  const opener = feed?.opener ?? null;
  // S0's contacts chip IS the "Bring your ppl" path; the other modules keep their slots.
  const deskModules = isS0 ? modules.filter((m) => !(m.type === 'setup' && m.payload.kind === 'contacts')) : modules;
  const showColumn = !loading && (deskModules.length > 0 || !!undo);
  const more = feed && !expanded ? feed.more : 0;

  return (
    <DeskFrame centred={centred}>
      {/* zone A */}
      <div className={cx('home-page-head', stale && 'home-page-head--stamped')}>
        <HomePill label={feed?.pillLabel || HOME_STATIC.pillDefault} />
        {stale && feed ? (
          <HomeReason tone="dim">
            {HOME_STATIC.asOf} {formatClock(feed.asOf)}
          </HomeReason>
        ) : null}
      </div>

      {/* zone B */}
      <div className="home-page-opener">
        {loading || openerPending ? (
          <HomeOpenerSkeleton />
        ) : opener ? (
          <HomeOpener text={opener.text} emphasis={opener.emphasis} size="hero" center={centred} dim={stale} onTap={opener.prompt ? () => props.onOpenerTap(opener) : undefined} />
        ) : (
          <HomeOpener text={HOME_STATIC.bareLine} size="hero" center={centred} dim={stale} />
        )}
      </div>

      {/* zone C */}
      {loading ? (
        <div className="home-page-desk">
          <HomeDeskSkeleton />
        </div>
      ) : null}
      {!loading && isS0 ? (
        <div className="home-page-desk">
          <S0Paths onMeet={props.onMeetPpl} />
        </div>
      ) : null}
      {showColumn ? (
        <div className={cx('home-page-desk', isS0 && 'home-page-desk--after-paths')}>
          <HomeDeskColumn stale={stale}>
            {deskModules.map((m, i) => (
              <HomeModuleView key={`${m.dismissKey}:${i}`} module={m} onAction={(a, mod) => props.onModuleAction(a, mod, i)} />
            ))}
            {undo ? <HomeUndoRow text={undo.text} onUndo={undo.onUndo} /> : null}
          </HomeDeskColumn>
        </div>
      ) : null}

      {/* zone D — nothing while loading, never placeholders */}
      {!loading && feed && feed.prompts.length ? (
        <div className="home-page-prompts">
          <HomePrompts prompts={feed.prompts} boxed={centred && !isS0} onPick={props.onPromptPick} onEdit={props.onPromptEdit} />
        </div>
      ) : null}

      {/* zone E */}
      {!loading && feed ? (
        <div className="home-page-more">
          <HomeMoreRow more={more} chats={chats} onClick={more > 0 ? props.onExpandOverflow : props.onPreviousChats} />
        </div>
      ) : null}

      {offline ? <div className="home-page-hint">{HOME_STATIC.offlineHint}</div> : null}
    </DeskFrame>
  );
}

// ── signed out (public route, canvas w2) ──────────────────────────────

export function SignedOutDesk({ onSend, onPrefill }: { onSend: (text: string) => void; onPrefill: (text: string) => void }) {
  const starters: string[] = [...HOME_STATIC.signedOutStarters];
  return (
    <DeskFrame centred>
      <div className="home-page-head">
        <HomePill label={HOME_STATIC.signedOutPill} />
      </div>
      <div className="home-page-opener">
        <HomeOpener text={HOME_STATIC.signedOutIntro} emphasis={HOME_STATIC.signedOutEmphasis} size="hero" center />
      </div>
      <div className="home-page-prompts">
        <HomePrompts
          prompts={starters}
          boxed
          onPick={(text, i) => {
            trackPromptTapped(null, i);
            onSend(text);
          }}
          onEdit={(text) => onPrefill(text)}
        />
      </div>
      <div className="home-page-hint">{HOME_STATIC.signedOutHint}</div>
    </DeskFrame>
  );
}

// ── the connected desk — what F8Chat mounts ───────────────────────────

export interface HomeDeskProps {
  /** Signed out (null) renders the public desk; nothing is fetched. */
  uid: string | null | undefined;
  /** The page's thread count — "N previous chats" in zone E. */
  chats?: number;
  /** Put text in the composer and focus it. Never auto-sends. */
  onPrefill: (text: string) => void;
  /** Send a message (prompt tap). Signed out, the page turns this into the login redirect. */
  onSend: (text: string) => void;
  /**
   * M7 — resume a thread in place (useF8Chat's loadThreadMessages). Without
   * it the desk navigates to /f8chat with `{ threadId }`, which the page
   * already honours.
   */
  onOpenThread?: (threadId: string) => void;
  /** Zone E "N previous chats". Default: the chat history page. */
  onPreviousChats?: () => void;
}

export default function HomeDesk({ uid, chats = 0, onPrefill, onSend, onOpenThread, onPreviousChats }: HomeDeskProps) {
  const navigate = useNavigate();
  const desk = useHomeDesk(uid ?? null);
  const { feed, dismiss, undoDismiss, expandOverflow, refresh } = desk;
  const stage = feed?.stage ?? null;

  const routerNavigate = useCallback((to: string, state?: Record<string, unknown>) => navigate(to, state ? { state } : undefined), [navigate]);
  const actions = useHomeActions({ navigate: routerNavigate, prefill: onPrefill, openThread: onOpenThread, dismiss, refresh, feed });

  const handleOpenerTap = useCallback(
    (opener: HomeOpenerData) => {
      if (stage) trackOpenerTapped(stage);
      onPrefill(opener.prompt);
    },
    [onPrefill, stage],
  );

  const handlePromptPick = useCallback(
    (text: string, index: number) => {
      trackPromptTapped(stage, index);
      onSend(text);
    },
    [onSend, stage],
  );

  const handlePromptEdit = useCallback((text: string) => onPrefill(text), [onPrefill]);

  const handleModuleAction = useCallback(
    (action: HomeModuleAction, module: HomeModule, position: number) => {
      if (!stage) return;
      // Resolve first so the tap is recorded with its real target, then run
      // it (a navigate unmounts this desk).
      const intent = actions.resolve(action, module);
      trackModuleTapped(stage, module, position, intentTarget(intent, action));
      actions.execute(intent, module);
    },
    [actions, stage],
  );

  const handlePreviousChats = useCallback(() => {
    if (onPreviousChats) onPreviousChats();
    else navigate('/chat-history');
  }, [navigate, onPreviousChats]);

  const handleMeetPpl = useCallback(() => navigate('/find-ppl'), [navigate]);

  if (!uid) return <SignedOutDesk onSend={onSend} onPrefill={onPrefill} />;

  return (
    <HomeInlineContext.Provider value={actions.inline}>
      <HomeDeskView
        status={desk.status}
        feed={feed}
        modules={desk.modules}
        openerPending={desk.openerPending}
        offline={desk.offline}
        expanded={desk.expanded}
        undo={desk.undo ? { text: desk.undo.text, onUndo: undoDismiss } : null}
        chats={chats}
        onOpenerTap={handleOpenerTap}
        onPromptPick={handlePromptPick}
        onPromptEdit={handlePromptEdit}
        onModuleAction={handleModuleAction}
        onExpandOverflow={expandOverflow}
        onPreviousChats={handlePreviousChats}
        onMeetPpl={handleMeetPpl}
      />
    </HomeInlineContext.Provider>
  );
}
